import { ExclamationCircleFilled } from '@ant-design/icons';
import { Flex, Modal, Table, Typography } from 'antd';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useSetUserActiveBranchMutation } from '@app/api/User/api';
import { getSocialIntegrationsColumns } from '@app/components/Modals/SocialIntegrationsWarningModal/data/columns';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { handleCatchError } from '@app/services';
import type { NSocialIntegrations } from '@app/services/rtkQuery/SocialIntegrations/types';
import {
  setActiveBranchCountryId,
  setActiveBranchName,
  setUser,
  useAppDispatch,
} from '@app/store';
import { setActiveBranchSession } from '@app/utils';

import '@app/components/Modals/SocialIntegrationsWarningModal/styles/SocialIntegrationsWarningModal.desktop.scss';

const { Text } = Typography;

interface Props {
  open: boolean;
  onClose: () => void;
  integrations: NSocialIntegrations.ModelSocialIntegrationError[];
}

const PF = 'social-integrations-warning-modal';

// Списка бывает много (у «GRUSHKA» 23 проблемные интеграции): окну нужна
// ширина под длинные названия филиалов и потолок высоты по экрану.
const MODAL_WIDTH = 1120;
const TABLE_MAX_HEIGHT = 'calc(100vh - 340px)';

export const SocialIntegrationsWarningModal = ({
  open,
  onClose,
  integrations,
}: Props) => {
  const [isLoading, setIsLoading] = useState(false);
  const { activeBranchId, user } = useUserPrint();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const [setUserActiveBranch] = useSetUserActiveBranchMutation();

  const openIntegration = useCallback(
    async (integration: NSocialIntegrations.ModelSocialIntegrationError) => {
      setIsLoading(true);

      try {
        const { BranchId, CountryId, Branch } = await setUserActiveBranch({
          ActiveBranchId: activeBranchId,
          branchId: integration.BranchId,
        }).unwrap();

        setActiveBranchSession({
          activeBranchId: BranchId,
          activeBranchName: Branch,
          activeBranchCountryId: CountryId,
        });
        dispatch(
          setUser({
            ...user,
            activeBranchId: BranchId,
          })
        );
        dispatch(setActiveBranchName(Branch));
        dispatch(setActiveBranchCountryId(CountryId));
        onClose();
        navigate('/integrations');
      } catch (error) {
        showNotification(handleCatchError(error));
      } finally {
        setIsLoading(false);
      }
    },
    [
      activeBranchId,
      dispatch,
      navigate,
      showNotification,
      user,
      onClose,
      setUserActiveBranch,
    ]
  );

  const columns = getSocialIntegrationsColumns({ openIntegration });

  return (
    <Modal
      title="Внимание! Возникли проблемы в разделе “Интеграции”"
      open={open}
      onCancel={onClose}
      footer={null}
      width={MODAL_WIDTH}
      destroyOnHidden
      classNames={{ body: `${PF}__body` }}
      loading={isLoading}
    >
      <Flex gap={16} align="center" justify="center" className="full-width">
        <ExclamationCircleFilled className={`${PF}__warning-icon`} />
        <Text>
          Отправка и получение сообщений в разделе Лиды и Клиенты могут не
          работать, так как обнаружены проблемы с интеграциями социальных сетей.
          Проверьте перечисленные ниже интеграции и в случае необходимости
          восстановите их работу.
        </Text>
      </Flex>

      {/* Скроллится только тело таблицы: заголовок окна, предупреждение и
          шапка таблицы остаются на месте, а страница под окном не уезжает
          вместе с ним (issue/11033). Высота считается от вьюпорта — на
          короткий список окно по-прежнему сжимается по содержимому. */}
      <Table<NSocialIntegrations.ModelSocialIntegrationError>
        rowKey={({ IntegrationId }) => String(IntegrationId)}
        columns={columns}
        dataSource={integrations}
        pagination={false}
        size="small"
        scroll={{ y: TABLE_MAX_HEIGHT }}
      />
    </Modal>
  );
};
